(() => {
  "use strict";


  /* =======================================================
     CONFIG
     ======================================================= */

  const PROXY_BASE =
    "/apps/mystery-vault";

  const VAULT_SLUG =
    "vault-001";

  const DESKTOP_AUTO_OPEN_MS =
    30000;

  const MOBILE_AUTO_OPEN_MS =
    40000;

  const TEASER_DELAY_MS =
    6000;

  const EXIT_INTENT_MIN_MS =
    10000;

  const DISMISS_COOLDOWN_MS =
    24 * 60 * 60 * 1000;

  const SESSION_TOKEN_KEY =
    "ml_mystery_vault_session_vault_001";

  const DISMISSED_KEY =
    "ml_mystery_vault_dismissed_vault_001";

  const PENDING_SUBSCRIPTION_KEY =
    "ml_mystery_vault_pending_subscription_vault_001";

  const SUBSCRIPTION_CONFIRMED_KEY =
    "ml_mystery_vault_subscription_confirmed_vault_001";


  /* =======================================================
     DOM READY
     ======================================================= */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      init
    );

  } else {
    init();
  }


  /* =======================================================
     INIT
     ======================================================= */

  async function init() {
    const root =
      document.getElementById(
        "ml-mystery-vault"
      );


    if (!root) {
      return;
    }


    root.hidden =
      true;


    /* =====================================================
       CHECK LIVE VAULT STATUS
       ===================================================== */

    let initialVaultStatus =
      null;


    try {
      const response =
        await fetch(
          `${PROXY_BASE}/status?vault=${encodeURIComponent(
            VAULT_SLUG
          )}`,
          {
            method:
              "GET",

            credentials:
              "same-origin",

            headers: {
              "Accept":
                "application/json"
            }
          }
        );


      initialVaultStatus =
        await response.json();

    } catch (error) {
      console.warn(
        "Mystery Vault status check failed:",
        error
      );

      return;
    }


    const initialRemaining =
      Number(
        initialVaultStatus &&
        initialVaultStatus.totals
          ? initialVaultStatus.totals.remaining
          : 0
      );


    const vaultIsActive =
      Boolean(
        initialVaultStatus &&
        initialVaultStatus.ok === true &&
        initialVaultStatus.vault &&
        initialVaultStatus.vault.status ===
          "active" &&
        Number.isFinite(
          initialRemaining
        ) &&
        initialRemaining > 0
      );


    if (!vaultIsActive) {
      return;
    }


    root.hidden =
      false;


    /* =====================================================
       DOM
       ===================================================== */

    const screens =
      Array.from(
        root.querySelectorAll(
          "[data-vault-screen]"
        )
      );


    const teaser =
      root.querySelector(
        "[data-vault-teaser]"
      );


    const form =
      root.querySelector(
        "#MLMysteryVaultNewsletter"
      );


    const emailInput =
      root.querySelector(
        "#ml-vault-email"
      );


    const formError =
      root.querySelector(
        "[data-vault-error]"
      );


    const unlockButton =
      root.querySelector(
        "[data-vault-unlock]"
      );


    const fatalMessage =
      root.querySelector(
        "[data-vault-fatal-message]"
      );


    const gameSearch =
      root.querySelector(
        "[data-vault-game-search]"
      );


    const gamesContainer =
      root.querySelector(
        "[data-vault-games]"
      );


    const freeError =
      root.querySelector(
        "[data-vault-free-error]"
      );


    const remainingWrap =
      root.querySelector(
        "[data-vault-remaining]"
      );


    const remainingCount =
      root.querySelector(
        "[data-vault-remaining-count]"
      );


    /* =====================================================
       INITIAL STOCK
       ===================================================== */

    if (
      Number.isFinite(
        initialRemaining
      )
    ) {
      if (remainingCount) {
        remainingCount.textContent =
          String(
            initialRemaining
          );
      }


      if (remainingWrap) {
        remainingWrap.hidden =
          false;
      }
    }


    /* =====================================================
       STATE
       ===================================================== */

    const state = {
      selectedLock:
        null,

      sessionToken:
        getSessionValue(
          SESSION_TOKEN_KEY
        ),

      reward:
        null,

      currentScreen:
        "locks",

      freeProducts:
        [],

      selectedGame:
        null,

      countdownTimer:
        null,

      autoOpenTimer:
        null,

      exitIntentEnabled:
        false,

      newsletterSubmitting:
        false
    };


    /* =====================================================
       STORAGE HELPERS
       ===================================================== */

    function getSessionValue(
      key
    ) {
      try {
        return sessionStorage.getItem(
          key
        );

      } catch (error) {
        return null;
      }
    }


    function setSessionValue(
      key,
      value
    ) {
      try {
        sessionStorage.setItem(
          key,
          value
        );

      } catch (error) {}
    }


    function removeSessionValue(
      key
    ) {
      try {
        sessionStorage.removeItem(
          key
        );

      } catch (error) {}
    }


    function subscriptionConfirmed() {
      return (
        getSessionValue(
          SUBSCRIPTION_CONFIRMED_KEY
        ) === "1"
      );
    }


    function subscriptionPending() {
      return (
        getSessionValue(
          PENDING_SUBSCRIPTION_KEY
        ) === "1"
      );
    }


    /* =====================================================
       GENERAL HELPERS
       ===================================================== */

    function isMobile() {
      return window.matchMedia(
        "(max-width: 760px)"
      ).matches;
    }


    function getDismissedAt() {
      try {
        const value =
          Number(
            localStorage.getItem(
              DISMISSED_KEY
            ) || 0
          );


        return Number.isFinite(
          value
        )
          ? value
          : 0;

      } catch (error) {
        return 0;
      }
    }


    function recentlyDismissed() {
      const dismissedAt =
        getDismissedAt();


      return (
        dismissedAt > 0 &&
        Date.now() -
          dismissedAt <
          DISMISS_COOLDOWN_MS
      );
    }


    function setDismissed() {
      try {
        localStorage.setItem(
          DISMISSED_KEY,
          String(
            Date.now()
          )
        );

      } catch (error) {}
    }


    function saveSessionToken(
      token
    ) {
      state.sessionToken =
        token;


      setSessionValue(
        SESSION_TOKEN_KEY,
        token
      );
    }


    function clearSessionToken() {
      state.sessionToken =
        null;


      removeSessionValue(
        SESSION_TOKEN_KEY
      );
    }


    function clearSubscriptionState() {
      removeSessionValue(
        PENDING_SUBSCRIPTION_KEY
      );

      removeSessionValue(
        SUBSCRIPTION_CONFIRMED_KEY
      );
    }


    function showError(
      element,
      message
    ) {
      if (!element) {
        return;
      }


      element.textContent =
        message;

      element.hidden =
        false;
    }


    function hideError(
      element
    ) {
      if (!element) {
        return;
      }


      element.hidden =
        true;

      element.textContent =
        "";
    }


    /* =====================================================
       SCREENS
       ===================================================== */

    function showScreen(
      screenName
    ) {
      state.currentScreen =
        screenName;


      screens.forEach(
        (screen) => {
          const active =
            screen.dataset.vaultScreen ===
            screenName;


          screen.hidden =
            !active;


          screen.classList.toggle(
            "is-active",
            active
          );
        }
      );


      const modal =
        root.querySelector(
          ".ml-vault__modal"
        );


      if (modal) {
        modal.scrollTop =
          0;
      }
    }


    /* =====================================================
       OPEN / CLOSE
       ===================================================== */

    function openVault() {
      root.classList.add(
        "is-open"
      );


      root.setAttribute(
        "aria-hidden",
        "false"
      );


      document.body.classList.add(
        "ml-vault-open"
      );


      if (teaser) {
        teaser.hidden =
          true;
      }


      window.setTimeout(
        () => {
          const focusable =
            root.querySelector(
              ".ml-vault__screen.is-active button, .ml-vault__screen.is-active input"
            );


          if (focusable) {
            focusable.focus({
              preventScroll:
                true
            });
          }
        },
        100
      );
    }


    function closeVault() {
      root.classList.remove(
        "is-open"
      );


      root.setAttribute(
        "aria-hidden",
        "true"
      );


      document.body.classList.remove(
        "ml-vault-open"
      );


      setDismissed();


      if (teaser) {
        teaser.hidden =
          false;
      }
    }


    /* =====================================================
       LOCK SELECTION
       ===================================================== */

    root
      .querySelectorAll(
        "[data-vault-lock]"
      )
      .forEach(
        (button) => {
          button.addEventListener(
            "click",
            () => {
              const lock =
                Number(
                  button.dataset.vaultLock
                );


              if (
                !Number.isInteger(
                  lock
                ) ||
                lock < 1 ||
                lock > 5
              ) {
                return;
              }


              state.selectedLock =
                lock;


              root
                .querySelectorAll(
                  "[data-vault-lock]"
                )
                .forEach(
                  (item) => {
                    item.classList.toggle(
                      "is-selected",
                      item === button
                    );
                  }
                );


              const selectedNumber =
                root.querySelector(
                  "[data-vault-selected-lock]"
                );


              if (selectedNumber) {
                selectedNumber.textContent =
                  String(
                    lock
                  );
              }


              window.setTimeout(
                () => {
                  showScreen(
                    "email"
                  );


                  if (emailInput) {
                    emailInput.focus();
                  }
                },
                180
              );
            }
          );
        }
      );


    /* =====================================================
       BACK
       ===================================================== */

    root
      .querySelectorAll(
        "[data-vault-back]"
      )
      .forEach(
        (button) => {
          button.addEventListener(
            "click",
            () => {
              const target =
                button.dataset.vaultBack;


              if (target) {
                showScreen(
                  target
                );
              }
            }
          );
        }
      );


    /* =====================================================
       CLOSE
       ===================================================== */

    root
      .querySelectorAll(
        "[data-vault-close]"
      )
      .forEach(
        (button) => {
          button.addEventListener(
            "click",
            closeVault
          );
        }
      );


    if (teaser) {
      teaser.addEventListener(
        "click",
        async () => {
          openVault();


          if (
            state.sessionToken &&
            subscriptionConfirmed()
          ) {
            await restoreExistingReward();
          }
        }
      );
    }


    /* =====================================================
       ESCAPE KEY
       ===================================================== */

    document.addEventListener(
      "keydown",
      (event) => {
        if (
          event.key === "Escape" &&
          root.classList.contains(
            "is-open"
          )
        ) {
          closeVault();
        }
      }
    );


    /* =====================================================
       EMAIL FORM
       ===================================================== */

    if (form) {
      form.addEventListener(
        "submit",
        async (
          event
        ) => {
          /*
           * If Shopify's native submission is already
           * underway, don't intercept it again.
           */
          if (
            state.newsletterSubmitting
          ) {
            return;
          }


          event.preventDefault();


          hideError(
            formError
          );


          const honeypot =
            form.querySelector(
              '[name="website"]'
            );


          if (
            honeypot &&
            honeypot.value
          ) {
            return;
          }


          const email =
            String(
              emailInput
                ? emailInput.value
                : ""
            )
              .trim()
              .toLowerCase();


          if (
            !isValidEmail(
              email
            )
          ) {
            showError(
              formError,
              "Please enter a valid email address."
            );

            return;
          }


          if (
            !state.selectedLock
          ) {
            showError(
              formError,
              "Please choose a lock first."
            );

            return;
          }


          setUnlockLoading(
            true
          );


          try {
            /*
             * 1. Create/validate the Mystery Vault play.
             * No reward is consumed at this stage.
             */
            const startData =
              await fetchJson(
                `${PROXY_BASE}/start`,
                {
                  method:
                    "POST",

                  body:
                    JSON.stringify({
                      email:
                        email,

                      lock:
                        state.selectedLock,

                      vault:
                        VAULT_SLUG
                    })
                }
              );


            if (
              !startData.ok
            ) {
              if (
                startData.code ===
                "ALREADY_PLAYED"
              ) {
                throw new Error(
                  "This email has already unlocked a Mystery Vault reward."
                );
              }


              throw new Error(
                startData.error ||
                "The Vault could not be opened."
              );
            }


            saveSessionToken(
              startData.session_token
            );


            /*
             * The visitor has not been confirmed as a
             * Shopify subscriber yet.
             */
            removeSessionValue(
              SUBSCRIPTION_CONFIRMED_KEY
            );


            setSessionValue(
              PENDING_SUBSCRIPTION_KEY,
              "1"
            );


            /*
             * 2. Submit the REAL native Shopify customer
             * form through Shopify's CAPTCHA protection.
             *
             * The browser then navigates away briefly.
             * Shopify returns us to:
             * ?mlvault_subscribed=1
             */
            submitNewsletterThroughShopify();

          } catch (error) {
            removeSessionValue(
              PENDING_SUBSCRIPTION_KEY
            );


            showScreen(
              "email"
            );


            showError(
              formError,
              error.message ||
              "Something went wrong. Please try again."
            );


            setUnlockLoading(
              false
            );
          }
        }
      );
    }


    function setUnlockLoading(
      loading
    ) {
      if (!unlockButton) {
        return;
      }


      unlockButton.disabled =
        loading;


      const label =
        unlockButton.querySelector(
          "span:first-child"
        );


      if (label) {
        label.textContent =
          loading
            ? "Joining & unlocking..."
            : "Join & Unlock My Reward";
      }
    }


    /* =====================================================
       SHOPIFY NEWSLETTER + HCAPTCHA
       ===================================================== */

    function submitNewsletterThroughShopify() {
      if (!form) {
        throw new Error(
          "Email signup is temporarily unavailable."
        );
      }


      const nativeSubmit =
        () => {
          /*
           * .submit() intentionally bypasses our own submit
           * listener after Shopify CAPTCHA has prepared the
           * form.
           */
          HTMLFormElement
            .prototype
            .submit
            .call(
              form
            );
        };


      if (
        window.Shopify &&
        window.Shopify.captcha &&
        typeof window.Shopify
          .captcha
          .protect ===
          "function"
      ) {
        window.Shopify
          .captcha
          .protect(
            form,
            () => {
              state.newsletterSubmitting =
                true;

              nativeSubmit();
            }
          );

        return;
      }


      /*
       * Fallback:
       * the Liquid form also has
       * data-shopify-captcha="true", so Shopify normally
       * wires CAPTCHA when the page loads.
       */
      state.newsletterSubmitting =
        true;


      nativeSubmit();
    }


    /* =====================================================
       RESUME AFTER SUCCESSFUL SHOPIFY SIGNUP
       ===================================================== */

    async function resumeAfterNewsletterSignup() {
      const url =
        new URL(
          window.location.href
        );


      const returnedFromSignup =
        url.searchParams.get(
          "mlvault_subscribed"
        ) === "1";


      if (
        !returnedFromSignup
      ) {
        /*
         * Don't allow an unfinished subscription flow to
         * become a shortcut to /reveal.
         */
        if (
          state.sessionToken &&
          subscriptionPending() &&
          !subscriptionConfirmed()
        ) {
          clearSessionToken();

          removeSessionValue(
            PENDING_SUBSCRIPTION_KEY
          );
        }

        return false;
      }


      /*
       * Remove the marker from the visible URL immediately.
       */
      url.searchParams.delete(
        "mlvault_subscribed"
      );


      window.history.replaceState(
        {},
        document.title,
        url.pathname +
          url.search +
          url.hash
      );


      if (
        !state.sessionToken ||
        !subscriptionPending()
      ) {
        return false;
      }


      /*
       * Shopify returned to the URL that exists only on
       * successful newsletter submission.
       */
      removeSessionValue(
        PENDING_SUBSCRIPTION_KEY
      );


      setSessionValue(
        SUBSCRIPTION_CONFIRMED_KEY,
        "1"
      );


      openVault();


      showScreen(
        "loading"
      );


      try {
        const minimumAnimation =
          sleep(
            900
          );


        const revealPromise =
          revealReward();


        await minimumAnimation;

        await revealPromise;

      } catch (error) {
        showFatal(
          error.message ||
          "Your reward could not be revealed. Please try again."
        );
      }


      return true;
    }


    /* =====================================================
       REVEAL
       ===================================================== */

    async function revealReward() {
      if (
        !state.sessionToken
      ) {
        throw new Error(
          "Your Vault session could not be found."
        );
      }


      if (
        !subscriptionConfirmed()
      ) {
        throw new Error(
          "Please complete the email signup before unlocking your reward."
        );
      }


      const data =
        await fetchJson(
          `${PROXY_BASE}/reveal`,
          {
            method:
              "POST",

            body:
              JSON.stringify({
                session_token:
                  state.sessionToken
              })
          }
        );


      if (
        !data.ok
      ) {
        if (
          data.code ===
          "INVALID_SESSION"
        ) {
          clearSessionToken();

          clearSubscriptionState();
        }


        throw new Error(
          data.error ||
          "Your reward could not be revealed."
        );
      }


      state.reward =
        data.reward ||
        null;


      /* ===============================================
         UPDATE REMAINING STOCK
         =============================================== */

      if (
        Array.isArray(
          data.remaining
        )
      ) {
        const totalRemaining =
          data.remaining.reduce(
            (
              total,
              prize
            ) =>
              total +
              Number(
                prize.remaining_quantity ||
                0
              ),
            0
          );


        if (remainingCount) {
          remainingCount.textContent =
            String(
              totalRemaining
            );
        }


        if (remainingWrap) {
          remainingWrap.hidden =
            false;
        }
      }


      /* ===============================================
         FREE GAME
         =============================================== */

      if (
        data.requires_selection ||
        (
          state.reward &&
          state.reward.reward_kind ===
            "free_game" &&
          state.reward.requires_selection
        )
      ) {
        await showFreeGamePicker(
          state.reward
        );

        return;
      }


      renderReward(
        state.reward
      );
    }


    /* =====================================================
       RESTORE AN ALREADY REVEALED REWARD
       ===================================================== */

    async function restoreExistingReward() {
      if (
        !state.sessionToken ||
        !subscriptionConfirmed()
      ) {
        return;
      }


      showScreen(
        "loading"
      );


      try {
        await revealReward();

      } catch (error) {
        clearSessionToken();

        clearSubscriptionState();

        showScreen(
          "locks"
        );
      }
    }


    /* =====================================================
       NORMAL REWARD
       ===================================================== */

    function renderReward(
      reward
    ) {
      if (
        !reward ||
        !reward.discount_code
      ) {
        showFatal(
          "Your reward was reserved, but the discount code is not ready yet. Please try again."
        );

        return;
      }


      const title =
        root.querySelector(
          "[data-vault-reward-title]"
        );


      const description =
        root.querySelector(
          "[data-vault-reward-description]"
        );


      const code =
        root.querySelector(
          "[data-vault-code]"
        );


      const shop =
        root.querySelector(
          "[data-vault-shop]"
        );


      if (title) {
        title.textContent =
          reward.label ||
          "Mystery Reward";
      }


      if (description) {
        description.textContent =
          rewardDescription(
            reward
          );
      }


      if (code) {
        code.textContent =
          reward.discount_code;
      }


      if (shop) {
        shop.href =
          buildDiscountUrl(
            reward.discount_code,
            "/collections/printable-escape-rooms"
          );
      }


      startCountdown(
        reward.discount_expires_at,
        root.querySelector(
          "[data-vault-countdown]"
        )
      );


      showScreen(
        "reward"
      );
    }


    function rewardDescription(
      reward
    ) {
      switch (
        reward.code
      ) {
        case "percent_20":
          return "Enjoy 20% off eligible printable escape rooms.";

        case "percent_25":
          return "Enjoy 25% off eligible printable escape rooms.";

        case "percent_30":
          return "Enjoy 30% off eligible printable escape rooms.";

        case "bogo":
          return "Add two eligible escape rooms to your cart and one will be free.";

        case "buy_2_get_1":
          return "Add three eligible escape rooms to your cart and one will be free.";

        default:
          return "Your Mystery Vault reward is ready to use.";
      }
    }


    /* =====================================================
       COPY NORMAL CODE
       ===================================================== */

    const copyButton =
      root.querySelector(
        "[data-vault-copy]"
      );


    if (copyButton) {
      copyButton.addEventListener(
        "click",
        async () => {
          const code =
            root.querySelector(
              "[data-vault-code]"
            );


          if (!code) {
            return;
          }


          await copyText(
            code.textContent
          );


          flashCopied(
            copyButton
          );
        }
      );
    }


    /* =====================================================
       FREE GAME
       ===================================================== */

    async function showFreeGamePicker(
      reward
    ) {
      showScreen(
        "free-game"
      );


      hideError(
        freeError
      );


      startCountdown(
        reward
          ? reward.discount_expires_at
          : null,

        root.querySelector(
          "[data-vault-free-countdown]"
        )
      );


      if (
        state.freeProducts.length
      ) {
        renderFreeGames(
          state.freeProducts
        );

        return;
      }


      if (gamesContainer) {
        gamesContainer.innerHTML =
          '<div class="ml-vault__games-loading">Loading eligible games...</div>';
      }


      try {
        const data =
          await fetchJson(
            `${PROXY_BASE}/free-game/options`,
            {
              method:
                "POST",

              body:
                JSON.stringify({
                  session_token:
                    state.sessionToken
                })
            }
          );


        if (
          !data.ok
        ) {
          throw new Error(
            data.error ||
            "Eligible games could not be loaded."
          );
        }


        if (
          data.already_selected &&
          data.reward
        ) {
          renderClaimedFreeGame(
            data.reward
          );

          return;
        }


        state.freeProducts =
          Array.isArray(
            data.products
          )
            ? data.products
            : [];


        renderFreeGames(
          state.freeProducts
        );

      } catch (error) {
        showError(
          freeError,
          error.message ||
          "Eligible games could not be loaded."
        );


        if (gamesContainer) {
          gamesContainer.innerHTML =
            "";
        }
      }
    }


    /* =====================================================
       RENDER FREE GAMES
       ===================================================== */

    function renderFreeGames(
      products,
      searchTerm = ""
    ) {
      if (
        !gamesContainer
      ) {
        return;
      }


      const normalizedSearch =
        searchTerm
          .trim()
          .toLowerCase();


      const filtered =
        products.filter(
          (product) =>
            !normalizedSearch ||
            String(
              product.title || ""
            )
              .toLowerCase()
              .includes(
                normalizedSearch
              )
        );


      if (
        !filtered.length
      ) {
        gamesContainer.innerHTML =
          '<div class="ml-vault__games-loading">No matching games found.</div>';

        return;
      }


      gamesContainer.innerHTML =
        "";


      filtered.forEach(
        (product) => {
          const variants =
            (
              Array.isArray(
                product.variants
              )
                ? product.variants
                : []
            )
              .filter(
                (variant) =>
                  variant.available_for_sale !==
                    false &&
                  variant.availableForSale !==
                    false
              );


          if (
            !variants.length
          ) {
            return;
          }


          const card =
            document.createElement(
              "div"
            );


          card.className =
            "ml-vault__game-card";


          const info =
            document.createElement(
              "div"
            );


          info.className =
            "ml-vault__game-info";


          const title =
            document.createElement(
              "p"
            );


          title.className =
            "ml-vault__game-title";


          title.textContent =
            product.title;


          info.appendChild(
            title
          );


          let variantSelect =
            null;


          if (
            variants.length > 1
          ) {
            variantSelect =
              document.createElement(
                "select"
              );


            variantSelect.className =
              "ml-vault__variant-select";


            variants.forEach(
              (variant) => {
                const option =
                  document.createElement(
                    "option"
                  );


                option.value =
                  variant.id;


                option.textContent =
                  variant.title +
                  " — " +
                  formatPrice(
                    variant.price
                  );


                variantSelect.appendChild(
                  option
                );
              }
            );


            info.appendChild(
              variantSelect
            );

          } else {
            const meta =
              document.createElement(
                "div"
              );


            meta.className =
              "ml-vault__game-meta";


            meta.textContent =
              formatPrice(
                variants[0].price
              );


            info.appendChild(
              meta
            );
          }


          const choose =
            document.createElement(
              "button"
            );


          choose.type =
            "button";


          choose.className =
            "ml-vault__choose-game";


          choose.textContent =
            "Choose";


          choose.addEventListener(
            "click",
            async () => {
              const variantId =
                variantSelect
                  ? variantSelect.value
                  : variants[0].id;


              const variant =
                variants.find(
                  (item) =>
                    item.id ===
                    variantId
                );


              if (!variant) {
                return;
              }


              state.selectedGame = {
                product:
                  product,

                variant:
                  variant
              };


              await claimFreeGame(
                choose
              );
            }
          );


          card.appendChild(
            info
          );


          card.appendChild(
            choose
          );


          gamesContainer.appendChild(
            card
          );
        }
      );


      if (
        !gamesContainer.children.length
      ) {
        gamesContainer.innerHTML =
          '<div class="ml-vault__games-loading">No currently available games found.</div>';
      }
    }


    /* =====================================================
       SEARCH FREE GAMES
       ===================================================== */

    if (gameSearch) {
      gameSearch.addEventListener(
        "input",
        () => {
          renderFreeGames(
            state.freeProducts,
            gameSearch.value
          );
        }
      );
    }


    /* =====================================================
       CLAIM FREE GAME
       ===================================================== */

    async function claimFreeGame(
      button
    ) {
      hideError(
        freeError
      );


      if (
        !state.selectedGame ||
        !state.sessionToken
      ) {
        return;
      }


      button.disabled =
        true;


      button.textContent =
        "Claiming...";


      try {
        const data =
          await fetchJson(
            `${PROXY_BASE}/claim-free-game`,
            {
              method:
                "POST",

              body:
                JSON.stringify({
                  session_token:
                    state.sessionToken,

                  product_id:
                    state
                      .selectedGame
                      .product
                      .id,

                  variant_id:
                    state
                      .selectedGame
                      .variant
                      .id
                })
            }
          );


        if (
          !data.ok
        ) {
          throw new Error(
            data.error ||
            "Your free game could not be claimed."
          );
        }


        renderClaimedFreeGame(
          data.reward
        );

      } catch (error) {
        showError(
          freeError,
          error.message ||
          "Your free game could not be claimed."
        );


        button.disabled =
          false;


        button.textContent =
          "Choose";
      }
    }


    /* =====================================================
       FREE GAME CLAIMED
       ===================================================== */

    function renderClaimedFreeGame(
      reward
    ) {
      state.reward =
        reward;


      const title =
        root.querySelector(
          "[data-vault-free-title]"
        );


      const code =
        root.querySelector(
          "[data-vault-free-code]"
        );


      const shop =
        root.querySelector(
          "[data-vault-free-shop]"
        );


      const selectedGame =
        reward
          ? reward.selected_game
          : null;


      if (title) {
        title.textContent =
          selectedGame &&
          selectedGame.product_title
            ? selectedGame.product_title
            : reward &&
              reward.selected_product_title
              ? reward.selected_product_title
              : "Your Free Escape Room";
      }


      if (code) {
        code.textContent =
          reward &&
          reward.discount_code
            ? reward.discount_code
            : "";
      }


      let redirect =
        "/collections/printable-escape-rooms";


      if (
        state.selectedGame &&
        state.selectedGame.product &&
        state.selectedGame.product.handle
      ) {
        redirect =
          "/products/" +
          state.selectedGame
            .product
            .handle;
      }


      if (
        shop &&
        reward &&
        reward.discount_code
      ) {
        shop.href =
          buildDiscountUrl(
            reward.discount_code,
            redirect
          );
      }


      startCountdown(
        reward
          ? reward.discount_expires_at
          : null,

        root.querySelector(
          "[data-vault-free-claimed-countdown]"
        )
      );


      showScreen(
        "free-claimed"
      );
    }


    /* =====================================================
       COPY FREE CODE
       ===================================================== */

    const freeCopyButton =
      root.querySelector(
        "[data-vault-free-copy]"
      );


    if (
      freeCopyButton
    ) {
      freeCopyButton.addEventListener(
        "click",
        async () => {
          const code =
            root.querySelector(
              "[data-vault-free-code]"
            );


          if (!code) {
            return;
          }


          await copyText(
            code.textContent
          );


          flashCopied(
            freeCopyButton
          );
        }
      );
    }


    /* =====================================================
       FATAL ERROR
       ===================================================== */

    function showFatal(
      message
    ) {
      if (fatalMessage) {
        fatalMessage.textContent =
          message;
      }


      showScreen(
        "fatal-error"
      );
    }


    const retryButton =
      root.querySelector(
        "[data-vault-retry]"
      );


    if (retryButton) {
      retryButton.addEventListener(
        "click",
        async () => {
          if (
            state.sessionToken &&
            subscriptionConfirmed()
          ) {
            showScreen(
              "loading"
            );


            try {
              await revealReward();

            } catch (error) {
              showFatal(
                error.message ||
                "Please try again."
              );
            }

          } else {
            showScreen(
              "locks"
            );
          }
        }
      );
    }


    /* =====================================================
       COUNTDOWN
       ===================================================== */

    function startCountdown(
      expiry,
      element
    ) {
      if (
        state.countdownTimer
      ) {
        clearInterval(
          state.countdownTimer
        );


        state.countdownTimer =
          null;
      }


      if (
        !element ||
        !expiry
      ) {
        return;
      }


      const expiresAt =
        new Date(
          expiry
        ).getTime();


      function update() {
        const remaining =
          expiresAt -
          Date.now();


        if (
          remaining <= 0
        ) {
          element.textContent =
            "Expired";


          clearInterval(
            state.countdownTimer
          );


          state.countdownTimer =
            null;

          return;
        }


        const hours =
          Math.floor(
            remaining /
            3600000
          );


        const minutes =
          Math.floor(
            (
              remaining %
              3600000
            ) /
            60000
          );


        const seconds =
          Math.floor(
            (
              remaining %
              60000
            ) /
            1000
          );


        element.textContent =
          String(
            hours
          ).padStart(
            2,
            "0"
          ) +
          ":" +
          String(
            minutes
          ).padStart(
            2,
            "0"
          ) +
          ":" +
          String(
            seconds
          ).padStart(
            2,
            "0"
          );
      }


      update();


      state.countdownTimer =
        window.setInterval(
          update,
          1000
        );
    }


    /* =====================================================
       NETWORK
       ===================================================== */

    async function fetchJson(
      url,
      options = {}
    ) {
      const response =
        await fetch(
          url,
          {
            ...options,

            headers: {
              "Content-Type":
                "application/json",

              ...(
                options.headers ||
                {}
              )
            },

            credentials:
              "same-origin"
          }
        );


      let data;


      try {
        data =
          await response.json();

      } catch (error) {
        throw new Error(
          "The Mystery Vault returned an invalid response."
        );
      }


      return data;
    }


    /* =====================================================
       COPY
       ===================================================== */

    async function copyText(
      text
    ) {
      if (
        navigator.clipboard &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(
          text
        );

        return;
      }


      const textarea =
        document.createElement(
          "textarea"
        );


      textarea.value =
        text;


      textarea.style.position =
        "fixed";


      textarea.style.opacity =
        "0";


      document.body.appendChild(
        textarea
      );


      textarea.select();


      document.execCommand(
        "copy"
      );


      textarea.remove();
    }


    function flashCopied(
      button
    ) {
      const original =
        button.textContent;


      button.textContent =
        "Copied!";


      window.setTimeout(
        () => {
          button.textContent =
            original;
        },
        1500
      );
    }


    /* =====================================================
       DISCOUNT URL
       ===================================================== */

    function buildDiscountUrl(
      code,
      redirect
    ) {
      return (
        "/discount/" +
        encodeURIComponent(
          code
        ) +
        "?redirect=" +
        encodeURIComponent(
          redirect
        )
      );
    }


    /* =====================================================
       PRICE
       ===================================================== */

    function formatPrice(
      price
    ) {
      const number =
        Number(
          price
        );


      if (
        !Number.isFinite(
          number
        )
      ) {
        return "";
      }


      try {
        return new Intl.NumberFormat(
          document.documentElement.lang ||
          "en",
          {
            style:
              "currency",

            currency:
              "EUR"
          }
        ).format(
          number
        );

      } catch (error) {
        return (
          "€" +
          number.toFixed(
            2
          )
        );
      }
    }


    /* =====================================================
       EMAIL VALIDATION
       ===================================================== */

    function isValidEmail(
      email
    ) {
      return (
        email.length <=
          254 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          .test(
            email
          )
      );
    }


    /* =====================================================
       SLEEP
       ===================================================== */

    function sleep(
      milliseconds
    ) {
      return new Promise(
        (resolve) =>
          window.setTimeout(
            resolve,
            milliseconds
          )
      );
    }


    /* =====================================================
       AUTO OPEN
       ===================================================== */

    function scheduleAutoOpen() {
      if (
        recentlyDismissed()
      ) {
        return;
      }


      const delay =
        isMobile()
          ? MOBILE_AUTO_OPEN_MS
          : DESKTOP_AUTO_OPEN_MS;


      state.autoOpenTimer =
        window.setTimeout(
          () => {
            if (
              !root.classList.contains(
                "is-open"
              )
            ) {
              openVault();


              if (
                state.sessionToken &&
                subscriptionConfirmed()
              ) {
                restoreExistingReward();
              }
            }
          },
          delay
        );
    }


    /* =====================================================
       EXIT INTENT — DESKTOP
       ===================================================== */

    function setupExitIntent() {
      if (
        isMobile() ||
        recentlyDismissed()
      ) {
        return;
      }


      window.setTimeout(
        () => {
          state.exitIntentEnabled =
            true;
        },
        EXIT_INTENT_MIN_MS
      );


      document.addEventListener(
        "mouseout",
        (event) => {
          if (
            !state.exitIntentEnabled ||
            root.classList.contains(
              "is-open"
            ) ||
            recentlyDismissed()
          ) {
            return;
          }


          if (
            event.clientY <= 0 &&
            !event.relatedTarget
          ) {
            openVault();


            state.exitIntentEnabled =
              false;


            if (
              state.sessionToken &&
              subscriptionConfirmed()
            ) {
              restoreExistingReward();
            }
          }
        }
      );
    }


    /* =====================================================
       BOOT
       ===================================================== */

    const resumed =
      await resumeAfterNewsletterSignup();


    if (!resumed) {
      window.setTimeout(
        () => {
          if (
            teaser &&
            !root.classList.contains(
              "is-open"
            )
          ) {
            teaser.hidden =
              false;
          }
        },
        TEASER_DELAY_MS
      );


      scheduleAutoOpen();

      setupExitIntent();
    }


    /* =====================================================
       DEBUG API
       ===================================================== */

    window.MysteryVault = {
      open:
        openVault,

      close:
        closeVault,

      show:
        showScreen,

      resetUiState:
        () => {
          clearSessionToken();

          clearSubscriptionState();


          try {
            localStorage.removeItem(
              DISMISSED_KEY
            );

          } catch (error) {}


          state.selectedLock =
            null;

          state.reward =
            null;

          state.freeProducts =
            [];

          state.selectedGame =
            null;


          showScreen(
            "locks"
          );


          if (teaser) {
            teaser.hidden =
              false;
          }
        }
    };
  }
})();